<!-- Loading State (checking Agremo status) -->
<ng-container *ngIf="isConfigured() === null">
  <div class="d-flex justify-content-between align-items-start mb-4">
    <div>
      <h1 class="h2 mb-1 fw-bold">Field Analyses</h1>
      <p class="text-muted mb-0">Advanced crop analysis from drone imagery</p>
    </div>
  </div>
  <div class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>
</ng-container>

<!-- Coming Soon State (Agremo not configured) -->
<ng-container *ngIf="isConfigured() === false">
  <div class="d-flex justify-content-between align-items-start mb-4">
    <div>
      <h1 class="h2 mb-1 fw-bold">Field Analyses</h1>
      <p class="text-muted mb-0">Advanced crop analysis from drone imagery</p>
    </div>
  </div>

  <div class="card">
    <div class="card-body p-5 text-center">
      <div class="mb-4">
        <i class="bi bi-graph-up-arrow hero-icon"></i>
      </div>
      <h4 class="fw-bold mb-3">Crop Analysis Coming Soon</h4>
      <p class="text-muted mb-3 mx-auto mw-500px">
        Powered by <a href="https://www.agremo.com/" target="_blank" rel="noopener noreferrer">Agremo</a>,
        field analysis will let you detect weeds, count plants, measure canopy cover, and more &mdash;
        all from your drone imagery.
      </p>
      <div class="row g-3 mt-4 justify-content-center mx-auto mw-700px">
        <div class="col-6 col-md-3">
          <div class="p-3 rounded border">
            <i class="bi bi-flower1 d-block mb-1 text-success fs-1-5rem"></i>
            <small>Weed Detection</small>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="p-3 rounded border">
            <i class="bi bi-hash d-block mb-1 text-primary fs-1-5rem"></i>
            <small>Stand Count</small>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="p-3 rounded border">
            <i class="bi bi-tree d-block mb-1 text-success fs-1-5rem"></i>
            <small>Canopy Cover</small>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="p-3 rounded border">
            <i class="bi bi-activity d-block mb-1 text-info fs-1-5rem"></i>
            <small>Plant Vigor</small>
          </div>
        </div>
      </div>
    </div>
  </div>
</ng-container>

<!-- Main Content (Agremo configured) -->
<ng-container *ngIf="isConfigured() === true">
  <!-- Page Header -->
  <div class="d-flex justify-content-between align-items-start mb-4">
    <div>
      <h1 class="h2 mb-1 fw-bold">Field Analyses</h1>
      <p class="text-muted mb-0">Request and manage crop analysis from drone imagery</p>
      <p class="text-muted small mb-0 mt-1">Analysis processing can take up to 24 hours depending on field size and analysis type.</p>
      <p class="text-muted small mb-0 mt-1">Powered by <a href="https://www.agremo.com/" target="_blank" rel="noopener noreferrer">Agremo</a></p>
    </div>
    <button class="btn btn-primary" (click)="addAnalysis()">
      <i class="bi bi-plus-lg me-2"></i>New Analysis
    </button>
  </div>

  <!-- Stats Summary -->
  <div class="row g-3 mb-4">
    <div class="col-md-4">
      <div class="card stat-card">
        <div class="card-body">
          <div class="d-flex align-items-center gap-3">
            <div class="stat-icon bg-success-subtle">
              <i class="bi bi-check-circle text-success"></i>
            </div>
            <div>
              <div class="stat-value">{{ completedCount() }}</div>
              <div class="stat-label text-muted">Completed</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-md-4">
      <div class="card stat-card">
        <div class="card-body">
          <div class="d-flex align-items-center gap-3">
            <div class="stat-icon bg-warning-subtle">
              <i class="bi bi-arrow-repeat text-warning"></i>
            </div>
            <div>
              <div class="stat-value">{{ processingCount() }}</div>
              <div class="stat-label text-muted">In Progress</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-md-4">
      <div class="card stat-card">
        <div class="card-body">
          <div class="d-flex align-items-center gap-3">
            <div class="stat-icon bg-danger-subtle">
              <i class="bi bi-exclamation-triangle text-danger"></i>
            </div>
            <div>
              <div class="stat-value">{{ failedCount() }}</div>
              <div class="stat-label text-muted">Failed</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Analyses List -->
  <div class="card">
    <div class="card-header p-3">
      <div class="d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Analyses</h5>
        <div class="d-flex gap-2">
          <button
            class="btn btn-sm"
            [class.btn-primary]="isFilterOpen() || activeFilterCount() > 0"
            [class.btn-outline-secondary]="!isFilterOpen() && activeFilterCount() === 0"
            (click)="toggleFilter()"
          >
            <i class="bi bi-funnel me-1"></i>Filter
            <span *ngIf="activeFilterCount() > 0" class="badge bg-white text-primary ms-1">{{ activeFilterCount() }}</span>
          </button>
        </div>
      </div>

      <!-- Filter Panel -->
      <div *ngIf="isFilterOpen()">
        <hr class="my-3">
        <div class="row g-3">
          <!-- Status Filter -->
          <div class="col-md-4">
            <label class="form-label small">Status</label>
            <select class="form-select form-select-sm" [(ngModel)]="filters.status" (ngModelChange)="updateActiveFilterCount()">
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="queued">Queued</option>
              <option value="processing">Processing</option>
              <option value="downloading">Downloading Results</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <!-- Date Range -->
          <div class="col-md-4">
            <label class="form-label small">From Date</label>
            <input
              type="date"
              class="form-control form-control-sm"
              [(ngModel)]="filters.dateFrom"
              (ngModelChange)="updateActiveFilterCount()"
            >
          </div>
          <div class="col-md-4">
            <label class="form-label small">To Date</label>
            <input
              type="date"
              class="form-control form-control-sm"
              [(ngModel)]="filters.dateTo"
              (ngModelChange)="updateActiveFilterCount()"
            >
          </div>
        </div>

        <!-- Clear Filters -->
        <div class="d-flex justify-content-between align-items-center mt-3">
          <span class="text-muted small">
            Showing {{ filteredAnalyses().length }} of {{ analyses().length }} analyses
          </span>
          <button
            *ngIf="activeFilterCount() > 0"
            class="btn btn-sm btn-link text-decoration-none"
            (click)="clearFilters()"
          >
            <i class="bi bi-x-lg me-1"></i>Clear Filters
          </button>
        </div>
      </div>
    </div>

    <div class="card-body p-0">
      <!-- Loading State -->
      <div *ngIf="isLoading() && analyses().length === 0" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-muted mt-2">Loading analyses...</p>
      </div>

      <!-- Error State -->
      <div *ngIf="error()" class="alert alert-danger m-3">
        <i class="bi bi-exclamation-triangle me-2"></i>{{ error() }}
      </div>

      <!-- Analyses Table -->
      <ng-container *ngIf="!isLoading() && filteredAnalyses().length > 0; else emptyState">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th class="ps-3">Field</th>
                <th>Analysis Type</th>
                <th>Status</th>
                <th>Requested</th>
                <th class="text-end pe-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let analysis of filteredAnalyses(); trackBy: trackByAnalysisId"
                  class="record-row"
                  role="button"
                  (click)="viewAnalysis(analysis)">
                <td class="ps-3">
                  <span class="fw-medium">{{ analysis.fieldName || 'Unknown Field' }}</span>
                  <div *ngIf="analysis.analysisParams?.cropType" class="text-muted small">
                    {{ analysis.analysisParams?.cropType }}
                  </div>
                </td>
                <td>
                  <span>{{ analysis.analysisTypeName || 'Analysis' }}</span>
                </td>
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge" [ngClass]="getStatusBadgeClass(analysis.status)">
                      <i [class]="getStatusIcon(analysis.status)" class="me-1"></i>
                      {{ getStatusText(analysis.status) }}
                    </span>
                    <!-- Progress bar for processing analyses -->
                    <div *ngIf="isProcessing(analysis.status) && analysis.progress > 0"
                         class="progress flex-grow-1 h-6px mw-80px">
                      <div class="progress-bar progress-bar-striped progress-bar-animated"
                           [style.width.%]="analysis.progress">
                      </div>
                    </div>
                    <span *ngIf="isProcessing(analysis.status) && analysis.progress > 0" class="small text-muted">
                      {{ analysis.progress }}%
                    </span>
                  </div>
                </td>
                <td>
                  <span class="fw-medium">{{ analysis.requestedAt | adnDate }}</span>
                </td>
                <td class="text-end pe-3">
                  <button class="btn btn-sm btn-outline-secondary" (click)="viewAnalysis(analysis); $event.stopPropagation()" title="View details">
                    <i class="bi bi-eye"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ng-container>

      <ng-template #emptyState>
        <div *ngIf="!isLoading() && !error()" class="p-5 text-center">
          <ng-container *ngIf="analyses().length === 0; else noResults">
            <div class="empty-state-icon mb-3">
              <i class="bi bi-graph-up"></i>
            </div>
            <h5>No Analyses Yet</h5>
            <p class="text-muted mb-3">Get started by requesting your first crop analysis from drone imagery.</p>
            <button class="btn btn-primary" (click)="addAnalysis()">
              <i class="bi bi-plus-lg me-2"></i>Request Analysis
            </button>
          </ng-container>
          <ng-template #noResults>
            <div class="empty-state-icon mb-3">
              <i class="bi bi-funnel"></i>
            </div>
            <h5>No Analyses Match Filters</h5>
            <p class="text-muted mb-3">Try adjusting your filters to see more results.</p>
            <button class="btn btn-outline-primary" (click)="clearFilters()">
              <i class="bi bi-x-lg me-2"></i>Clear Filters
            </button>
          </ng-template>
        </div>
      </ng-template>
    </div>
  </div>
</ng-container>
